import { AxiosError }        from 'axios';
import {
  UseQueryOptions,
  useQuery,
  UseMutationOptions,
  useMutation,
  UseQueryResult,
}                           from 'react-query';
import moment               from 'moment';
import { IVinDecodeResult } from '@utils/useDecodeVinCode';
import { callApi }          from '@utils/apiCaller';
import { Notification }     from '@components/Notification/Notification';
import {
  showErrorMessage,
  parseRecords,
}                           from '@utils/commonFunctions';
import {
  ISearchResult,
  ISearchQueryParams,
}                           from '@typings/search';
import {
  IAuction,
  IPhoto,
  IAuctionDetails,
  IAuctionsResponse,
  IUploadAuctionPhotoPayload,
  IDeleteAuctionPhotoPayload,
  IFetchBid,
  IRotateAuctionPhotoPayload,
  IUsageAuctionsPayload,
  IUsageAuctions,
  IAuctionFacetsStats,
  TStatusOption,
}                           from '@typings/auction';

// #region FETCH AUCTIONS STATUSES
const fetchAuctionsStatuses = async (filters?: string): Promise<TStatusOption[]> => {
  try {
    const params: ISearchQueryParams = {
      facets : 'Status',
      size   : 0,
      filters,
    };

    const { data } = await callApi<ISearchResult<IAuction, IAuctionFacetsStats>>(
      'v2/management/auctions',
      'get',
      params,
    );

    return data.facetsStats.status ?? [];
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchAuctionsStatuses = (
  filters? : string,
  config?  : UseQueryOptions<TStatusOption[], Error>,
) => useQuery(
  ['auctions-facets', filters],
  () => fetchAuctionsStatuses(filters),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH AUCTIONS
const fetchAuctions = async (searchParams: ISearchQueryParams): Promise<IAuctionsResponse> => {
  try {
    const { data } = await callApi<ISearchResult<IAuction>>(
      'v2/management/auctions',
      'get',
      searchParams,
    );

    return {
      totalRecordsFound : data.totalRecordsFound,
      auctions          : parseRecords<IAuction>(data),
    };
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchAuctions = (
  searchParams : ISearchQueryParams,
  config?      : UseQueryOptions<IAuctionsResponse, Error>,
) => useQuery(
  ['auctions', searchParams],
  () => fetchAuctions(searchParams),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// @endregion

// #startregion FETCH AUCTION DETAILS
const fetchAuctionData = async (payload: string): Promise<IAuctionDetails> => {
  try {
    const { data } = await callApi<IAuction>(`auctions/auctionlot/${payload}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchAuctionData = (
  payload: string,
  config? : UseQueryOptions<IAuctionDetails, Error>,
) => useQuery(
  ['fetch-auction-details', payload],
  () => fetchAuctionData(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH AUCTION PHOTOS
const fetchAuctionPhotos = async (auctionId : string): Promise<IPhoto[]> =>  {
  try {
    const { data = [] } = await callApi<IPhoto[]>(`v2/intranet/photos/${auctionId}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchAuctionPhotos = (
  auctionId : string,
  config? : UseQueryOptions<IPhoto[], Error>,
) => useQuery(
  ['fetch-auction-photos', auctionId],
  () => fetchAuctionPhotos(auctionId),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPLOAD AUCTION PHOTO
const uploadAuctionPhoto = async ({
  auctionId,
  photoType,
  image,
}: IUploadAuctionPhotoPayload) => {
  try {
    const formData = new FormData();

    formData.append('file', image);

    const { data = {} as IPhoto } = await callApi(
      `v2/intranet/upload?auctionId=${auctionId}&photoType=${photoType}&rotationDeg=0`,
      'post',
      formData,
      { isFormData: true },
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUploadAuctionPhoto = (
  config?: UseMutationOptions<IPhoto, Error, IUploadAuctionPhotoPayload>,
) => useMutation(
  uploadAuctionPhoto,
  { ...config },
);
// #endregion

// #region DELETE AUCTION PHOTO
const deleteAuctionPhoto = async ({ auctionId, photoId }: IDeleteAuctionPhotoPayload) => {
  try {
    await callApi(
      `v2/intranet/photos/${auctionId}/${photoId}`,
      'delete',
    );

    Notification.enqueueSnackbar(
      'The photo has been deleted.',
      'info',
    );

    return photoId;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useDeleteAuctionPhoto = (
  config?: UseMutationOptions<string, Error, IDeleteAuctionPhotoPayload>,
) => useMutation(
  deleteAuctionPhoto,
  { ...config },
);
// #endregion

// #region UPDATE AUCTION PHOTO'S ROTATION_DEG
const rotateAuctionPhoto = async ({
  auctionId,
  photoId,
  degree,
}: IRotateAuctionPhotoPayload): Promise<IPhoto> => {
  try {
    const { data } = await callApi<IPhoto>(
      `v2/intranet/photos/${auctionId}/${photoId}/${degree}`,
      'put',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useRotateAuctionPhoto = (
  config?: UseMutationOptions<IPhoto, Error, IRotateAuctionPhotoPayload>,
) => useMutation(
  rotateAuctionPhoto,
  { ...config },
);
// #endregion

// #region FETCH NADA AUCTIONS
const fetchUsageAuctions = async ({ invoiceId, cuid }: IUsageAuctionsPayload) => {
  try {
    const { data = [] as IUsageAuctions[] } = await callApi(`intrainvoice/nada/usage/${cuid}/${invoiceId}`, 'get');

    return data;
  } catch (e) {
    return Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );
  }
};

export const useFetchUsageAuctions = (
  payload : IUsageAuctionsPayload,
  config? : UseQueryOptions<IUsageAuctions[], Error>,
) => useQuery(
  ['fetch-nada-auctions', payload.invoiceId, payload.cuid],
  () => fetchUsageAuctions(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH NADA AUCTIONS DETAILS
const fetchNadaDetailsAuctions = async (payload: IUsageAuctionsPayload) => {
  try {
    const { invoiceId, cuid } = payload;
    const { data = [] as IAuction[] } = await callApi(`intrainvoice/nada/usage/details/${cuid}/${invoiceId}`, 'get');

    return data;
  } catch (e) {
    return Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );
  }
};

export const useFetchNadaAuctions = (
  payload : IUsageAuctionsPayload,
  config? : UseQueryOptions<IAuction[]>,
) => useQuery(
  ['fetch-nada-details-auctions', payload],
  () => fetchNadaDetailsAuctions(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #startregion FETCH AUCTION BIDS
const fetchAuctionBidData = async (payload: string) => {
  try {
    const { data = {} as IAuction } = await callApi(`auctions/auctionlot/${payload}`);

    const updatedData = data.bids.reduce((acc: any, curr: any) => {
      acc.push({
        bidder  : `${curr.user?.firstName} ${curr.user?.lastName}`,
        amount  : curr.amount ?? null,
        bidDate : moment(curr?.dateCreated).format('MM/DD/YYYY') ?? '',
        bidTime : moment(curr?.dateCreated).format('hh:mm A'),
      });

      return acc;
    }, []);

    return updatedData.sort((a: any, b: any) => a.amount > b.amount);
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchAuctionBidData = (
  config?: UseMutationOptions<IFetchBid[], Error, string>,
) => useMutation(
  fetchAuctionBidData,
  { ...config },
);
// #endregion

// #region UPDATE AUCTION
const updateAuction = async ({ id, ...updatedValues }: Partial<IAuction>) => {
  try {
    await callApi(
      `v2/intranet/auctionlot/${id}`,
      'put',
      updatedValues,
    );

    Notification.enqueueSnackbar(
      'Auction updated.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );
  }
};

export const useUpdateAuction = (
  config: UseMutationOptions<void, Error, Partial<IAuction>> = {},
) => useMutation(
  updateAuction,
  { ...config },
);
// #endregion

// #region FETCH VIN DECODER INFO
const fetchVinData = async (payload: string): Promise<IVinDecodeResult> => {
  try {
    const { data } = await callApi<IVinDecodeResult>(`auctions/decodevin?vin=${payload}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchVinData = (
  vin     : string,
  config? : UseQueryOptions<IVinDecodeResult, AxiosError>,
): UseQueryResult<IVinDecodeResult, AxiosError> => useQuery(
  ['fetch-vin-data', vin],
  () => fetchVinData(vin),
  {
    refetchOnWindowFocus : false,
    retry                : false,
    ...config,
  },
);
// #endregion
